import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WorkflowTemplateService } from '../services/workflow-template-service';
import { CreateWorkflowTemplateRequest, UpdateWorkflowTemplateRequest } from '../types';
import httpClient from '../services/http-client';

const templateService = new WorkflowTemplateService(httpClient);

export const workflowTemplateQueryKeys = {
  all: ['workflow-templates'] as const,
  list: (divisionId: string, active?: boolean) => [...workflowTemplateQueryKeys.all, 'list', divisionId, active] as const,
  detail: (divisionId: string, id: string) => [...workflowTemplateQueryKeys.all, 'detail', divisionId, id] as const,
};

export function useWorkflowTemplates(divisionId: string, active?: boolean, enabled = true) {
  return useQuery({
    queryKey: workflowTemplateQueryKeys.list(divisionId, active),
    queryFn: () => templateService.getTemplates(divisionId, active),
    enabled: enabled && !!divisionId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useWorkflowTemplate(divisionId: string, id: string, enabled = true) {
  return useQuery({
    queryKey: workflowTemplateQueryKeys.detail(divisionId, id),
    queryFn: () => templateService.getTemplate(divisionId, id),
    enabled: enabled && !!divisionId && !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateWorkflowTemplate(divisionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateWorkflowTemplateRequest) =>
      templateService.createTemplate(divisionId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...workflowTemplateQueryKeys.all, 'list', divisionId] });
    },
  });
}

export function useUpdateWorkflowTemplate(divisionId: string, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateWorkflowTemplateRequest) =>
      templateService.updateTemplate(divisionId, id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...workflowTemplateQueryKeys.all, 'list', divisionId] });
      queryClient.invalidateQueries({ queryKey: workflowTemplateQueryKeys.detail(divisionId, id) });
    },
  });
}

export function useDeleteWorkflowTemplate(divisionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => templateService.deleteTemplate(divisionId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...workflowTemplateQueryKeys.all, 'list', divisionId] });
    },
  });
}
